import { useState, type ReactNode } from "react";
import {
  Button,
  ConfirmModal,
  ErrorState,
  FormField,
  IconButton,
  LoadingSkeleton,
  TextInput,
  useToast,
} from "@a2z/ui";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  SellerCustomDomainStatus,
  SellerCustomDomainView,
} from "@/lib/api/types";
import { useCustomDomainQuery } from "@/modules/custom-domain/services/useCustomDomainQuery";
import {
  useAddCustomDomainMutation,
  useConfirmCustomDomainMutation,
  useRefreshCustomDomainMutation,
  useRemoveCustomDomainMutation,
} from "@/modules/custom-domain/services/useCustomDomainMutations";
import { Card, Icons } from "./primitives";
import { getDomainProvider } from "./custom-domain-providers";

const cd = keys.seller.customDomain;

// Server-driven status → dot + label (never colour alone). Tones use semantic
// tokens: success = live, error = needs attention, primary = in progress,
// neutral = not started.
type StatusTone = { bg: string; fg: string; dot: string };
const STATUS_TONE: Record<SellerCustomDomainStatus, StatusTone> = {
  active: {
    bg: "var(--success-100)",
    fg: "var(--success-text)",
    dot: "var(--success-500)",
  },
  error: {
    bg: "var(--error-100)",
    fg: "var(--error-500)",
    dot: "var(--error-500)",
  },
  provisioning: {
    bg: "var(--surface-alt)",
    fg: "var(--text-secondary)",
    dot: "var(--primary-500)",
  },
  pending: {
    bg: "var(--surface-alt)",
    fg: "var(--text-secondary)",
    dot: "var(--accent-500)",
  },
};

export function CustomDomainPanel({ storeSlug }: { storeSlug: string }) {
  const t = useTranslations();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useCustomDomainQuery({
    storeSlug,
  });

  if (isLoading && !data) {
    return <LoadingSkeleton variant="card" rows={2} />;
  }
  if (error) {
    return <ErrorState onRetry={() => void refetch()} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {data ? (
        <ConnectedDomain
          storeSlug={storeSlug}
          domain={data}
          onActionError={() => toast.error(t(keys.common.error))}
          onCopy={() => toast.success(t(cd.copiedToast))}
          onCopyError={() => toast.error(t(keys.common.error))}
          onRemoved={() => toast.success(t(cd.removedToast))}
          onRemoveError={() => toast.error(t(keys.common.error))}
        />
      ) : (
        <AddDomainForm
          storeSlug={storeSlug}
          onAdded={() => toast.success(t(cd.addedToast))}
        />
      )}
    </div>
  );
}

function StatusPill({
  status,
  label,
}: {
  status: SellerCustomDomainStatus;
  label: string;
}) {
  const tone = STATUS_TONE[status];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
      style={{ background: tone.bg, color: tone.fg }}
    >
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: tone.dot }}
      />
      {label}
    </span>
  );
}

// Small rotating spinner (reduced-motion: freezes). Colour via `currentColor`.
function Spinner({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg
      className={`${className} animate-spin motion-reduce:animate-none`}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}


// ── Empty state: explain what this is, what you need, and what happens next ──
function AddDomainForm({
  storeSlug,
  onAdded,
}: {
  storeSlug: string;
  onAdded: () => void;
}) {
  const t = useTranslations();
  const { mutateAsync: addDomain, isPending } = useAddCustomDomainMutation();
  const [hostname, setHostname] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  const trimmed = hostname.trim().toLowerCase();
  const looksValid = /^[a-z0-9.-]+\.[a-z]{2,}$/.test(trimmed);

  const submit = async (): Promise<void> => {
    if (isPending) return;
    if (!looksValid) {
      setFieldError(t(cd.invalidHostname));
      return;
    }
    setFieldError(null);
    try {
      await addDomain({ storeSlug, hostname: trimmed });
      setHostname("");
      onAdded();
    } catch (err) {
      setFieldError(errorMessage(err) ?? t(keys.common.error));
    }
  };

  return (
    <Card title={t(cd.introTitle)} desc={t(cd.introBody)}>
      <div className="flex flex-col gap-5">
        <InfoNote title={t(cd.needTitle)}>{t(cd.needBody)}</InfoNote>

        <div className="flex flex-col gap-3" style={{ maxWidth: 420 }}>
          <FormField
            label={t(cd.hostnameLabel)}
            name="hostname"
            error={fieldError ?? undefined}
            helperText={fieldError ? undefined : t(cd.hostnameHelper)}
          >
            <TextInput
              name="hostname"
              value={hostname}
              placeholder={t(cd.hostnamePlaceholder)}
              hasError={Boolean(fieldError)}
              inputMode="url"
              autoComplete="off"
              autoCapitalize="none"
              dir="ltr"
              onChange={(e) => {
                setHostname(e.target.value);
                if (fieldError) setFieldError(null);
              }}
            />
          </FormField>
          <div>
            <Button
              variant="primary"
              size="md"
              onClick={() => void submit()}
              disabled={!looksValid || isPending}
            >
              {t(cd.addButton)}
            </Button>
          </div>
        </div>

        <InfoNote title={t(cd.whatNextTitle)}>{t(cd.whatNextBody)}</InfoNote>
      </div>
    </Card>
  );
}

function ConnectedDomain({
  storeSlug,
  domain,
  onActionError,
  onCopy,
  onCopyError,
  onRemoved,
  onRemoveError,
}: {
  storeSlug: string;
  domain: SellerCustomDomainView;
  onActionError: () => void;
  onCopy: () => void;
  onCopyError: () => void;
  onRemoved: () => void;
  onRemoveError: () => void;
}) {
  const t = useTranslations();
  const { mutateAsync: removeDomain, isPending: isRemoving } =
    useRemoveCustomDomainMutation();
  const { mutateAsync: refreshDomain, isPending: isRefreshing } =
    useRefreshCustomDomainMutation();
  const { mutateAsync: confirmDomain, isPending: isConfirming } =
    useConfirmCustomDomainMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const statusLabels: Record<SellerCustomDomainStatus, string> = {
    pending: t(cd.statusPending),
    provisioning: t(cd.statusProvisioning),
    active: t(cd.statusActive),
    error: t(cd.statusError),
  };

  const copy = async (value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      onCopy();
    } catch {
      onCopyError();
    }
  };

  const remove = async (): Promise<void> => {
    try {
      await removeDomain({ storeSlug });
      setConfirmOpen(false);
      onRemoved();
    } catch {
      onRemoveError();
    }
  };

  const isActive = domain.status === "active";
  const isError = domain.status === "error";
  const isPending = domain.status === "pending"; // awaiting the seller's confirm
  const isProvisioning = domain.status === "provisioning"; // verifying
  // Show the setup steps until the domain is live (in error too, so they can
  // recheck the record).
  const showSteps = !isActive;

  // Provider-specific field wording (GoDaddy "Host/Points to", etc.) when we
  // detected the seller's DNS provider; generic hints otherwise.
  const provider = getDomainProvider(domain.dnsProvider);
  const nameHelp = provider
    ? t(cd.dnsFieldHelp, { field: provider.nameField })
    : t(cd.dnsNameHelp);
  const valueHelp = provider
    ? t(cd.dnsFieldHelp, { field: provider.valueField })
    : t(cd.dnsValueHelp);

  // "We're doing X" line from the raw CF ssl status — a sense of real progress
  // (DNS validation → certificate issuance) while setting up.
  const setupMessage = (() => {
    const s = (domain.sslStatus ?? "").toLowerCase();
    if (s.includes("issuance") || s.includes("deployment")) {
      return t(cd.setupMsgCert);
    }
    if (s.includes("validation") || s === "initializing" || s === "") {
      return t(cd.setupMsgDns);
    }
    return t(cd.setupMsgGeneric);
  })();

  return (
    <>
      {/* Status header */}
      <Card
        title={<span dir="ltr">{domain.hostname}</span>}
        desc={
          isActive
            ? t(cd.activeBody, { hostname: domain.hostname })
            : isError
              ? t(cd.errorBody)
              : isPending
                ? t(cd.pendingBody)
                : t(cd.provisioningBody)
        }
        action={
          <StatusPill
            status={domain.status}
            label={statusLabels[domain.status]}
          />
        }
      >
        {isActive ? (
          <a
            href={`https://${domain.hostname}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 self-start rounded-lg border border-[color:var(--border-light)] px-3 py-1.5 text-[13px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-alt)]"
          >
            {t(cd.visitButton)}
            <span aria-hidden="true">↗</span>
          </a>
        ) : (
          <div className="flex flex-col items-start gap-2">
            {isProvisioning ? (
              <div className="flex items-center gap-2 text-[color:var(--primary-500)]">
                <Spinner className="h-4 w-4" />
                <span className="text-[13px] font-medium text-[color:var(--text-primary)]">
                  {setupMessage}
                </span>
              </div>
            ) : null}
            <p className="text-[12.5px] leading-relaxed text-[color:var(--text-muted)]">
              {isError
                ? t(cd.errorRecheckHint)
                : isPending
                  ? t(cd.pendingHint)
                  : t(cd.provisioningWaitHint)}
            </p>
          </div>
        )}
      </Card>

      {/* Guided DNS setup */}
      {showSteps ? (
        <Card title={t(cd.setupTitle)} desc={t(cd.setupIntro)}>
          {provider ? (
            <div className="mb-4 flex flex-col items-start gap-1 rounded-lg bg-[color:var(--surface-alt)] px-3.5 py-3">
              <p className="text-[12.5px] font-medium text-[color:var(--text-secondary)]">
                {t(cd.providerDetected, { provider: provider.name })}
              </p>
              <a
                href={provider.helpUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[12.5px] font-medium text-[color:var(--primary-600)] hover:underline"
              >
                {t(cd.providerHelpLink, { provider: provider.name })}
                <span aria-hidden="true">↗</span>
              </a>
            </div>
          ) : null}
          <ol className="m-0 flex list-none flex-col gap-4 p-0">
            <Step index={1}>
              {t(cd.step1, { hostname: domain.hostname })}
            </Step>
            <Step index={2}>{t(cd.step2)}</Step>
            <Step index={3}>
              <div className="flex flex-col gap-3">
                <span>{t(cd.step3)}</span>
                <div className="flex flex-col gap-3">
                  <DnsRow
                    label={t(cd.dnsType)}
                    help={t(cd.dnsTypeHelp)}
                    value={domain.dns.type}
                  />
                  <DnsRow
                    label={t(cd.dnsName)}
                    help={nameHelp}
                    value={domain.dns.name}
                    copyLabel={t(cd.copyButton)}
                    onCopy={() => void copy(domain.dns.name)}
                  />
                  <DnsRow
                    label={t(cd.dnsValue)}
                    help={valueHelp}
                    value={domain.dns.value}
                    copyLabel={t(cd.copyButton)}
                    onCopy={() => void copy(domain.dns.value)}
                  />
                </div>
              </div>
            </Step>
            <Step index={4}>{t(cd.step4)}</Step>
          </ol>

          <div className="mt-4 rounded-lg bg-[color:var(--surface-alt)] px-3 py-2.5">
            <p className="text-[12px] leading-relaxed text-[color:var(--text-muted)]">
              {isPending ? t(cd.setupReassurePending) : t(cd.setupReassure)}
            </p>
          </div>

          <div className="mt-4">
            {isPending ? (
              <Button
                type="button"
                variant="primary"
                size="md"
                isLoading={isConfirming}
                disabled={isConfirming}
                onClick={() => {
                  void confirmDomain({ storeSlug }).catch(onActionError);
                }}
              >
                {t(cd.confirmButton)}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                isLoading={isRefreshing}
                disabled={isRefreshing}
                leadingIcon={
                  isRefreshing ? <Spinner className="h-3.5 w-3.5" /> : undefined
                }
                onClick={() => {
                  void refreshDomain({ storeSlug }).catch(onActionError);
                }}
              >
                {t(cd.refreshButton)}
              </Button>
            )}
          </div>
        </Card>
      ) : null}

      {/* Remove */}
      <div className="flex flex-col items-start gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          disabled={isRemoving}
        >
          {t(cd.removeButton)}
        </Button>
        <p className="text-[11.5px] leading-snug text-[color:var(--text-muted)]">
          {t(cd.removeReassure)}
        </p>
      </div>

      <ConfirmModal
        isOpen={confirmOpen}
        title={t(cd.removeConfirmTitle)}
        description={t(cd.removeConfirmBody)}
        confirmLabel={t(cd.removeButton)}
        cancelLabel={t(keys.common.cancel)}
        tone="danger"
        isSubmitting={isRemoving}
        onConfirm={() => void remove()}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}

// Numbered step: a circular index + content. Uses logical spacing so it flips
// correctly in RTL.
function Step({ index, children }: { index: number; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--surface-alt)] text-[12px] font-bold text-[color:var(--text-secondary)]"
      >
        {index}
      </span>
      <div className="flex-1 pt-0.5 text-[13px] leading-relaxed text-[color:var(--text-primary)]">
        {children}
      </div>
    </li>
  );
}

// Neutral explainer note (what-you-need / what-next) on an inset surface.
function InfoNote({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg bg-[color:var(--surface-alt)] px-3.5 py-3">
      <p className="text-[12px] font-semibold text-[color:var(--text-secondary)]">
        {title}
      </p>
      <p className="mt-1 text-[12.5px] leading-relaxed text-[color:var(--text-muted)]">
        {children}
      </p>
    </div>
  );
}

function DnsRow({
  label,
  help,
  value,
  onCopy,
  copyLabel,
}: {
  label: string;
  help?: string;
  value: string;
  onCopy?: () => void;
  copyLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold text-[color:var(--text-secondary)]">
        {label}
        {help ? (
          <span className="ms-1 font-normal text-[color:var(--text-muted)]">
            — {help}
          </span>
        ) : null}
      </span>
      <div className="flex items-center gap-2">
        <code
          dir="ltr"
          className="min-w-0 flex-1 break-all rounded-lg bg-[color:var(--surface-alt)] px-3 py-2 font-mono text-[13px] text-[color:var(--text-primary)]"
        >
          {value}
        </code>
        {onCopy && copyLabel ? (
          <IconButton
            icon={Icons.copy}
            ariaLabel={copyLabel}
            variant="ghost"
            size="md"
            onClick={onCopy}
          />
        ) : null}
      </div>
    </div>
  );
}

function errorMessage(err: unknown): string | null {
  if (err && typeof err === "object" && "message" in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string" && message.length > 0) {
      return message;
    }
  }
  return null;
}
